import axios, { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

interface Config {
  headers: {
    Authorization: string;
    DeviceId: string | null;
    PlatformID: string | null;
    site: string;
  };
}

export const useFetchApi = (url: string) => {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useState(() => {
    return null;
  });

  return {
    data,
    error,
    isLoading,
  };
};

export const useFetchApiWithRedux = (url: string, reducer: any) => {
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        const token = localStorage.getItem("accessToken");

        if (!token) {
          throw new Error("Access token not found");
        }

        const config = {
          signal: controller.signal,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
            DeviceId: localStorage.getItem("deviceID"),
            PlatformID: localStorage.getItem("platform"),
            site: "mobile-site",
          },
        };

        const response = await axios.get(url, config);

        if (response.data.status === 200) {
          dispatch(reducer(response.data));
          setError(null);
        } else {
          navigate("/page403", {
            replace: true,
          });
        }
      } catch (error: any) {
        if (controller.signal.aborted) {
          return;
        }

        setError(error);
        dispatch(reducer(null));
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    void fetchData();

    return () => {
      controller.abort();
    };
  }, [url, navigate, dispatch, reducer]);

  return {
    error,
    isLoading,
  };
};

export const fetchApiFunctional = async <TResponse = any,>(
  url: string,
  signal?: AbortSignal,
): Promise<TResponse | undefined> => {
  const token = localStorage.getItem("accessToken");
  const config = {
    signal,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: `Bearer ${token}`,
      DeviceId: localStorage.getItem("deviceID"),
      PlatformID: localStorage.getItem("platform"),
      site: "mobile-site",
    },
  };

  try {
    const response = await axios.get<TResponse>(url, config);
    if (response.status === 200) {
      return response.data;
    }

    return undefined;
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }

    throw error;
  }
};

export default function usePost() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<AxiosError | null>(null);
  const [data, setData] = useState<any>(null);

  const token = localStorage.getItem("accessToken");
  const config: Config = {
    headers: {
      Authorization: `Bearer ${token}`,
      DeviceId: localStorage.getItem("deviceID"),
      PlatformID: localStorage.getItem("platform"),
      site: "mobile-site",
    },
  };

  const makeRequest = async (
    url: string,
    requestData: any,
    isResponseBlob: boolean = false,
    signal?: AbortSignal,
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(url, requestData, {
        ...config,
        signal,
        ...(isResponseBlob && {
          responseType: "blob",
        }),
      });

      setData(response.data);

      return response.data;
    } catch (error: any) {
      if (!signal?.aborted) {
        setError(error);
      }

      throw error;
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  return {
    makeRequest,
    data,
    isLoading,
    error,
  };
}

export const getHeader = () => {
  const token = localStorage.getItem("accessToken");
  const deviceID = localStorage.getItem("deviceID");
  const platform = localStorage.getItem("platform");

  if (!token || !deviceID || !platform) {
    const errors: string[] = [];

    if (!token) errors.push("Access token not found");
    if (!deviceID) errors.push("Device ID not found");
    if (!platform) errors.push("Platform not found");

    throw new Error(errors.join("\n"));
  }

  return {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      DeviceId: deviceID,
      PlatformID: platform,
      site: "mobile-site",
    },
  };
};

export const UsePatch = async (
  url: string,
  data?: object,
  signal?: AbortSignal,
) => {
  const config = getHeader();

  try {
    const response = await axios.patch(url, data, {
      ...config,
      signal,
    });

    if (response.status === 200) {
      return response.data;
    }
  } catch (error: any) {
    if (signal?.aborted) {
      throw error;
    }

    console.error("Error during PATCH request:", error.message);
    throw error;
  }
};

export const fetchApiWithParams = async <TResponse, TParams>(
  url: string,
  params?: TParams,
  signal?: AbortSignal,
): Promise<TResponse> => {
  const token = localStorage.getItem("accessToken");

  const config = {
    params,
    signal,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      DeviceId: localStorage.getItem("deviceID"),
      PlatformID: localStorage.getItem("platform"),
      site: "mobile-site",
    },
  };

  const response = await axios.get<TResponse>(url, config);

  if (response.status === 200) {
    return response.data;
  }

  throw new Error(`Request failed with status code ${response.status}`);
};

export const postApiFunctional = async <TResponse, TPayload>(
  url: string,
  payload: TPayload,
  signal?: AbortSignal,
): Promise<TResponse> => {
  const token = localStorage.getItem("accessToken");

  const config = {
    signal,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      DeviceId: localStorage.getItem("deviceID"),
      PlatformID: localStorage.getItem("platform"),
      site: "mobile-site",
    },
  };

  const response = await axios.post<TResponse>(url, payload, config);

  if (response.status === 200) {
    return response.data;
  }

  throw new Error(`Request failed with status code ${response.status}`);
};

export const putApiFunctional = async <TResponse, TPayload>(
  url: string,
  payload: TPayload,
  signal?: AbortSignal,
): Promise<TResponse> => {
  const token = localStorage.getItem("accessToken");

  const config = {
    signal,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      DeviceId: localStorage.getItem("deviceID"),
      PlatformID: localStorage.getItem("platform"),
      site: "mobile-site",
    },
  };

  const response = await axios.put<TResponse>(url, payload, config);

  if (response.status >= 200 && response.status < 300) {
    return response.data;
  }

  throw new Error(`Request failed with status code ${response.status}`);
};

export const patchApiFunctional = async <TResponse, TPayload>(
  url: string,
  payload: TPayload,
  signal?: AbortSignal,
): Promise<TResponse> => {
  const config = getHeader();

  const response = await axios.patch<TResponse>(url, payload, {
    ...config,
    signal,
  });

  if (response.status >= 200 && response.status < 300) {
    return response.data;
  }

  throw new Error(`Request failed with status code ${response.status}`);
};

export const getApiBlob = async (
  url: string,
  signal?: AbortSignal,
): Promise<{
  blob: Blob;
  contentDisposition: string | null;
}> => {
  const token = localStorage.getItem("accessToken");

  const config = {
    signal,
    responseType: "blob" as const,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      DeviceId: localStorage.getItem("deviceID"),
      PlatformID: localStorage.getItem("platform"),
      site: "mobile-site",
    },
  };

  const response = await axios.get<Blob>(url, config);

  if (response.status >= 200 && response.status < 300) {
    return {
      blob: response.data,
      contentDisposition: response.headers["content-disposition"] ?? null,
    };
  }

  throw new Error(`Request failed with status code ${response.status}`);
};
